// Загрузка и монтаж экрана сетлиста — вынесено из main.ts (дисциплина роста:
// монолит несёт только ветку маршрута). Две точки входа — сетлист со звуком
// (/setlist/<id>) и ссылка слов (/setlist/w/<uuid>) — сходятся в один вид
// (setlist-screen/view.ts).
import '../styles/setlist-screen.css';
import { fetchLibrary, type TeamLibrary } from '../library.js';
import { fetchPublishedSetlist, type SetlistMeta, type SetlistSong } from '../setlist.js';
import { fetchTextShare } from '../text-share/text-share-api.js';
import { fetchLiveAnchor } from '../live/live-api.js';
import type { LiveResult } from '../live/live-model.js';
import { formatServiceDate } from '../ui/format-date.js';
import { t, tn, getLocale } from '../ui/i18n.js';
import { renderFreshnessLine } from '../ui/freshness-line.js';
import { freshness } from '../offline/freshness.js';
import type { OfflineStore, CachedSetlist } from '../offline/offline-store.js';
import { SETLIST_KEY_TEAM } from '../offline/setlist-offline.js';
import { choiceKey, readChoice } from '../versions/version-choice.js';
import { liveWordsPath } from '../router.js';
import { mountSetlistScreen } from './view.js';
import { renderGuestSetlist, renderGuestClosed, type GuestSetlistVm, type GuestScreenRow } from './guest-view.js';
import { mergeLibraries, audioSetlistSongs, wordsSetlistSongs, buildScreenRows, songsFromSetlist, liveSongNumber } from './model.js';

export type ScreenOutcome =
  | { state: 'mounted'; unmount(): void }
  | { state: 'needs-auth' }
  | { state: 'not-found' }
  | { state: 'error' }
  | { state: 'redirect'; to: string };

interface Deps { apiBase: string; navigate: (path: string) => void; fetchImpl?: typeof fetch }

function subtitle(eventDate: string | null, createdAt: string, teamName: string): string {
  return [formatServiceDate(eventDate ?? createdAt, getLocale()), teamName || null].filter(Boolean).join(' · ');
}

/**
 * `fetchLiveAnchor` не заводит собственного таймаута (сеть — обычный fetch),
 * а якорь — необязательное украшение экрана, не повод держать монтаж вечно.
 * 3 с — минимум, закрывающий «висит навсегда», а не точная настройка: гонка
 * проигрывает состоянию `error`, которое `liveSongNumber` уже читает как
 * «лидер сейчас не ведёт».
 */
const LIVE_TIMEOUT_MS = 3000;

function timeoutFallback<T>(ms: number, value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

async function liveOf(textShareId: string | null, d: Deps): Promise<{ songNumber: number; path: string } | null> {
  if (!textShareId) return null;
  const res = await Promise.race([
    fetchLiveAnchor(textShareId, d.apiBase, d.fetchImpl),
    timeoutFallback<LiveResult>(LIVE_TIMEOUT_MS, { state: 'error' }),
  ]);
  const n = liveSongNumber(res);
  // `liveWordsPath`, не голый `/t/`: тот увёл бы обратно на этот же экран (B-261002).
  return n === null ? null : { songNumber: n, path: liveWordsPath(textShareId) };
}

export async function mountAudioSetlistScreen(host: HTMLElement, setlistId: string, d: Deps): Promise<ScreenOutcome> {
  const res = await fetchPublishedSetlist(setlistId, d.apiBase, d.fetchImpl);
  if (!res.ok) return res.needsAuth ? { state: 'needs-auth' } : res.notFound ? { state: 'not-found' } : { state: 'error' };
  // Библиотека может не ответить (сеть/401 повторно и т.п.) — экран всё равно
  // рисуется, просто у строк без совпадения в библиотеке слоты слов пусты.
  const libRes = await fetchLibrary(d.apiBase, d.fetchImpl);
  const teams: TeamLibrary[] = libRes.ok ? libRes.teams : [];
  const team = teams.find((t) => (t.setlists ?? []).some((s) => s.setlistId === setlistId));
  const summary = team?.setlists?.find((s) => s.setlistId === setlistId);
  // Единый сетлист, фаза 2: сервер отдаёт готовые песни (слова + все версии,
  // доступ уже применён) — библиотека для строк не нужна на этом пути. Ответ
  // без `songs` (старый воркер) или с пустым составом — прежний путь по
  // `items`+библиотеке, иначе сетлист из ссылки слов (`items` пуст) открылся
  // бы пустым экраном.
  const rows = res.songs && res.songs.length > 0
    ? songsFromSetlist(res.songs, setlistId)
    : buildScreenRows(audioSetlistSongs(res.items), mergeLibraries(teams), { kind: 'audio', setlistId });
  const view = mountSetlistScreen(host, {
    title: res.setlist.name,
    subtitle: subtitle(summary?.eventDate ?? null, res.setlist.createdAt, team?.teamName ?? ''),
    rows,
    live: await liveOf(res.setlist.liveId ?? summary?.textShareId ?? null, d),
    originPath: '/setlist/' + encodeURIComponent(setlistId),
    navigate: d.navigate,
    // Библиотека не ответила (401 у гостя на публичном сетлисте, либо любая
    // другая неудача) — вести «в библиотеку» некуда, кнопку не рисуем.
    showBack: libRes.ok,
  });
  return { state: 'mounted', unmount: view.unmount };
}

export async function mountWordsSetlistScreen(host: HTMLElement, textShareId: string, d: Deps): Promise<ScreenOutcome> {
  const libRes = await fetchLibrary(d.apiBase, d.fetchImpl);
  // Библиотека нужна здесь только чтобы найти команду/сетлист и подмешать
  // слова из него — сама страница слов `/t/<uuid>` работает без библиотеки
  // вовсе. 401 — обычное дело: гость читает ссылку слов, расшаренную в чат, и
  // входить ему незачем. Решение владельца продукта 23.09 (фаза 1): такой
  // гость должен продолжать видеть нынешнюю страницу `/t/`, а не упираться в
  // login gate. Любую другую неудачу библиотеки (сеть/5xx) уводим туда же по
  // той же причине — страница слов не завязана на библиотеку, и превращать
  // её сбой в тупик для читателя незачем; единственное, чего он лишится, —
  // подсветки, что ссылка на самом деле сидит в сетлисте со звуком.
  if (!libRes.ok) return { state: 'redirect', to: '/t/' + textShareId };
  const team = libRes.teams.find((t) => (t.textSetlists ?? []).some((s) => s.textShareId === textShareId));
  const summary = team?.textSetlists?.find((s) => s.textShareId === textShareId);
  if (!team || !summary) {
    // Ссылку могли склеить на сервере с сетлистом со звуком (единый сетлист,
    // фаза 1) — тогда она сидит не в `textSetlists[]`, а в
    // `setlists[].textShareId` того сетлиста. Уводить такую в `/t/` значило бы
    // показать читателю страницу без аудио, хотя у команды есть полный экран
    // с ним — редиректим на `/setlist/<setlistId>` вместо этого.
    const audioTeam = libRes.teams.find((t) => (t.setlists ?? []).some((s) => s.textShareId === textShareId));
    const audioSummary = audioTeam?.setlists?.find((s) => s.textShareId === textShareId);
    if (audioSummary) return { state: 'redirect', to: '/setlist/' + encodeURIComponent(audioSummary.setlistId) };
    // Не в команде этой ссылки вовсе (гость/чужая команда) или ссылка не
    // склеена ни с чем — читать её можно только нынешней страницей слов: она
    // и скажет «завершена» / «нет такой», у неё это уже есть.
    return { state: 'redirect', to: '/t/' + textShareId };
  }
  const payload = await fetchTextShare(textShareId, d.apiBase, d.fetchImpl);
  if (payload.state !== 'ok') return { state: 'redirect', to: '/t/' + textShareId };
  const rows = buildScreenRows(wordsSetlistSongs(summary.songIds, payload.payload), mergeLibraries(libRes.teams), { kind: 'words' });
  const view = mountSetlistScreen(host, {
    title: summary.name,
    subtitle: subtitle(summary.eventDate, summary.createdAt, team.teamName),
    rows,
    live: await liveOf(textShareId, d),
    originPath: '/setlist/w/' + encodeURIComponent(textShareId),
    navigate: d.navigate,
    // До /setlist/w/<uuid> не добраться без библиотеки (mountWordsSetlistScreen
    // уже вышел бы на needs-auth/redirect выше) — кнопка есть всегда.
    showBack: true,
  });
  return { state: 'mounted', unmount: view.unmount };
}

// ── /s/<uuid>[?g=<key>] — экран сетлиста участника/гостя (единый сетлист, ──
// ── фазы 3+4, задача D2) ─────────────────────────────────────────────────

export interface GuestScreenDeps extends Deps {
  /** Личность для персонального выбора версии/офлайна — readOfflineUserId()
   *  в main.ts. `null` — анонимное устройство (гость, ни разу не входивший
   *  здесь), выбор версии/офлайн-кэш тогда не читаются и не пишутся. */
  userId: string | null;
  /** Офлайн-кэш ЭТОЙ личности (openOfflineStore() в main.ts), или `null` —
   *  свежий телефон/приватный режим (кэша нет по построению). */
  offlineStore?: OfflineStore | null;
}

export interface MountGuestSetlistScreenArgs {
  root: HTMLElement;
  setlistId: string;
  /** `g` из query, или `null` — легаси-публичный сетлист читается без ключа
   *  (`SetlistAccess.via === 'legacy-public'`, setlist-access.ts воркера). */
  guestKey: string | null;
  deps: GuestScreenDeps;
}

// Ключ офлайн-кэша сетлиста этого экрана — `SETLIST_KEY_TEAM` из
// offline/setlist-offline.ts (её doc-комментарий несёт полное обоснование:
// team-agnostic ключ, ЕДИНЫЙ helper на весь код — offline/prefetch.ts
// (`prefetchSetlists`) и offline/reconcile.ts читают/пишут туда же).
// Локальный алиас — только чтобы не переписывать все вызовы ниже.
const OFFLINE_TEAM = SETLIST_KEY_TEAM;

function guestSubtitle(eventDate: string | null | undefined, createdAt: string, songsCount: number): string {
  return [formatServiceDate(eventDate ?? createdAt, getLocale()), tn('library.setlistSongs', songsCount)].filter(Boolean).join(' · ');
}

/** Подпись внизу гостевого экрана — только когда РОВНО один из двух флагов
 *  закрыт: `resolveSetlistAccess` (воркер) никогда не пускает гостя дальше
 *  401 с обоими флагами закрытыми (`if (f.wordsPublic || f.audioPublic)`), а
 *  «оба открыты» подписи не требует — там нечего объяснять. */
function guestCaption(access: { words: boolean; audio: boolean } | undefined): string | null {
  if (!access) return null;
  if (access.words && !access.audio) return t('guestSetlist.captionAudioTeam');
  if (!access.words && access.audio) return t('guestSetlist.captionWordsTeam');
  return null;
}

/** `?setlist=<id>[&g=<key>]` — гостевой пропуск словам ОДНОЙ песни
 *  (`GET /api/song-texts/:id?setlist&g`, guestWordsAccess). Участник за
 *  куки эти параметры не открывает вовсе (view.ts, `openWords`) — они нужны
 *  только там, где куки нет. */
function guestWordsPath(wordsId: string, setlistId: string, guestKey: string | null): string {
  const q = new URLSearchParams({ setlist: setlistId });
  if (guestKey) q.set('g', guestKey);
  return `/words/${encodeURIComponent(wordsId)}?${q.toString()}`;
}

/** Довешивает `&g=<key>` к уже готовому `?setlist=<id>` из `songsFromSetlist`
 *  (model.ts) — тот путь общий с участником и `g` не знает, потому что
 *  участнику он не нужен (кука уже пропуск). */
function withGuestKey(audioPath: string | null, guestKey: string | null): string | null {
  if (!audioPath || !guestKey) return audioPath;
  return `${audioPath}&g=${encodeURIComponent(guestKey)}`;
}

interface GuestSuccessPayload { setlist: SetlistMeta; songs: SetlistSong[]; access?: { words: boolean; audio: boolean; member: boolean } }

/** Куда ведёт «Sign in»/замок аудио: `/login?next=<этот же адрес>` — после
 *  входа та же ссылка (путь + `?g=`) читается СНОВА, и куки уже достаточно,
 *  чтобы получить полный, member-доступ (сервер решает по куке РАНЬШЕ
 *  гостевого ключа, setlist.ts/setlist-access.ts) — редиректить конкретно
 *  на `/s/<id>` без параметров незачем, текущий адрес и так им является.
 *
 *  Security fix (guest-link key in analytics, #12): `location.pathname +
 *  location.search` here — and the URL bar's `?g=` in general — is left
 *  intentionally UNSTRIPPED. A guest reloading this page, or adding it to
 *  the home screen, must keep working off the same `?g=` link (there is no
 *  other credential to fall back on). What changed instead is
 *  `posthog-init.ts`'s `sanitizeSecretPaths`, which now redacts the `g` query
 *  param before this `next=` value (and any other url-ish property) reaches
 *  PostHog — see its own comment for the three shapes this covers, including
 *  this exact `next=%2Fs%2F<id>%3Fg%3D<key>` percent-encoded one. */
function buildSignInHandler(navigate: (path: string) => void): () => void {
  return () => {
    const next = typeof location !== 'undefined' ? location.pathname + location.search : '/library';
    navigate('/login?next=' + encodeURIComponent(next));
  };
}

function mountFromPayload(
  root: HTMLElement,
  payload: GuestSuccessPayload,
  args: { setlistId: string; guestKey: string | null; deps: GuestScreenDeps; live: { songNumber: number; path: string } | null; offline: boolean },
): { unmount(): void } {
  const { setlistId, guestKey, deps, live, offline } = args;
  const access = payload.access ?? { words: true, audio: true, member: false };
  const onSignIn = buildSignInHandler(deps.navigate);
  const rows = songsFromSetlist(payload.songs, setlistId, {
    access,
    choice: (songId) => readChoice(localStorage, choiceKey(deps.userId, setlistId, songId)),
  });

  let unmount: () => void;
  if (access.member) {
    const view = mountSetlistScreen(root, {
      title: payload.setlist.name,
      subtitle: subtitle(payload.setlist.eventDate ?? null, payload.setlist.createdAt, payload.setlist.teamName ?? ''),
      rows,
      live,
      originPath: `/s/${encodeURIComponent(setlistId)}`,
      navigate: deps.navigate,
      showBack: true,
    });
    unmount = view.unmount;
  } else {
    const guestRows: GuestScreenRow[] = rows.map((r) => ({
      key: r.key,
      name: r.name,
      meta: r.meta,
      wordsPath: r.wordsId ? guestWordsPath(r.wordsId, setlistId, guestKey) : null,
      audioPath: withGuestKey(r.audioPath, guestKey),
      audioLocked: r.audioLocked,
    }));
    const vm: GuestSetlistVm = {
      title: payload.setlist.name,
      subtitle: guestSubtitle(payload.setlist.eventDate, payload.setlist.createdAt, guestRows.length),
      live,
      rows: guestRows,
      caption: guestCaption(access),
    };
    root.innerHTML = '';
    root.appendChild(renderGuestSetlist(vm, { navigate: deps.navigate, onSignIn }));
    unmount = () => { root.innerHTML = ''; };
  }
  if (offline) {
    const line = renderFreshnessLine(
      freshness({ online: false, syncing: false, lastSuccessAt: payload.setlist.updatedAt, nowMs: Date.now() }),
      getLocale(),
      Date.now(),
    );
    // insertBefore, не prepend(): у tsconfig.json стоит types:
    // ["@cloudflare/workers-types"], который в этом репо (см. те же ошибки в
    // rail-library-button.ts/waves.ts/invite-view.ts/chart-fullscreen.ts, ни
    // одного из них эта задача не трогает) сталкивает сигнатуру `.append()`/
    // `.prepend()` — существующая, не заведённая здесь порча типов.
    const screenRoot = root.firstElementChild;
    if (screenRoot) screenRoot.insertBefore(line, screenRoot.firstChild);
  }
  return { unmount };
}

/**
 * Загрузка и монтаж унифицированного экрана сетлиста — участник (кука) видит
 * тот же вид, что и `/setlist/<id>` (фаза 1), не-участник — гостевой вид
 * (design gate G2, `guest-view.ts`). Доступ решает СЕРВЕР
 * (`GET /api/setlists/:id[?g=]`, `resolveSetlistAccess`) — этот модуль только
 * раскладывает уже решённый ответ по двум видам, копий проверки доступа
 * здесь нет.
 */
export async function mountGuestSetlistScreen(args: MountGuestSetlistScreenArgs): Promise<ScreenOutcome> {
  const { root, setlistId, guestKey, deps } = args;
  const res = await fetchPublishedSetlist(setlistId, deps.apiBase, deps.fetchImpl, { guestKey });
  if (!res.ok) {
    // Единый сетлист, задача D4 — зеркало `applyFetchResult` (words-resolve.ts):
    // сервер СКАЗАЛ «этого сетлиста больше нет» (404) или «доступ закрылся»
    // (401) — офлайн-копия ЭТОЙ же ссылки не имеет права пережить это молча,
    // иначе следующий заход в офлайне показал бы состав, который человеку
    // больше не должен быть виден. Обычный сетевой/5xx отказ (ветка ниже,
    // `cached`) кэш не трогает — там он и есть смысл офлайна.
    if (res.needsAuth || res.notFound) {
      void deps.offlineStore?.delSetlist(OFFLINE_TEAM, setlistId).catch(() => {});
    }
    if (res.needsAuth) {
      // Ключ БЫЛ (в адресе стоял `?g=`), но сервер всё равно ответил 401 —
      // ключ неверный/отозван: гостя не пускают ко входу общей OTP-калиткой
      // (он не выбирал вход, у него была прямая ссылка), у него свой экран
      // «эта ссылка закрыта» с одной кнопкой входа для СВОИХ.
      if (guestKey) {
        root.innerHTML = '';
        root.appendChild(renderGuestClosed({ name: null }, { onSignIn: buildSignInHandler(deps.navigate) }));
        return { state: 'mounted', unmount: () => { root.innerHTML = ''; } };
      }
      // Без ключа вовсе — тот же случай, что у /setlist/<id>: анониму нечем
      // подтвердить, что он гость этой ссылки, экран решает main.ts (OTP-калитка).
      return { state: 'needs-auth' };
    }
    if (res.notFound) return { state: 'not-found' };
    // Сеть/5xx/иное — офлайн-снимок этой личности, если он есть (задача D2).
    const cached = deps.offlineStore ? await deps.offlineStore.loadSetlist(OFFLINE_TEAM, setlistId).catch(() => null) : null;
    if (cached) {
      const payload = cached.payload as GuestSuccessPayload;
      const mounted = mountFromPayload(root, payload, { setlistId, guestKey, deps, live: null, offline: true });
      return { state: 'mounted', unmount: mounted.unmount };
    }
    return { state: 'error' };
  }
  const payload: GuestSuccessPayload = { setlist: res.setlist, songs: res.songs ?? [], access: res.access };
  const live = res.setlist.liveId ? await liveOf(res.setlist.liveId, deps) : null;
  const mounted = mountFromPayload(root, payload, { setlistId, guestKey, deps, live, offline: false });
  // Лучшее-из-возможного сохранение снимка ДЛЯ ЭТОЙ ЖЕ ссылки — без личности
  // (анонимное устройство, `deps.offlineStore` не открыт) кэшировать негде и
  // незачем: следующий заход всё равно не сможет узнать, что это тот же
  // человек, что и сейчас (offline-store.ts, ключ строится по userId).
  void deps.offlineStore?.saveSetlist(OFFLINE_TEAM, setlistId, { updatedAt: res.setlist.updatedAt, payload } satisfies CachedSetlist).catch(() => {});
  return { state: 'mounted', unmount: mounted.unmount };
}
